import { Children, cloneElement, Component, createRef, isValidElement } from 'react';
import { DefaultTheme, ThemeProps, withTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { createPropsGetter, getComponentProps } from '@optimus-web/common';
import { ClickAwayProps, ScrollAwayProps, withClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { DropDownButtonVariant } from '@optimus-web/local-tokens';
import { Button } from '../Button';
import { ExpandIcon } from '../ExpandIcon';
import { ListItemProps } from '../ListItem';
import { Menu } from '../Menu';
import { SizeType } from '../types';
import { mod } from '../utils/func';
import { ContentElement } from './style';

export type DropDownButtonProps = {
    disabled?: boolean;
    icon?: IconName;
    label?: React.ReactNode;
    variant?: DropDownButtonVariant;
    portalContainerId?: string;
    size?: SizeType;
} & Partial<DropDownButtonDefaultProps> &
    React.ButtonHTMLAttributes<HTMLButtonElement>;

type DropDownButtonDefaultProps = typeof defaultProps;
const defaultProps = {
    wide: false,
};

const getProps = createPropsGetter(defaultProps);

interface State {
    opened: boolean;
}

type ChildProps = Pick<ListItemProps, 'innerRef' | 'tabIndex' | 'role'> & {
    onClick?: (event: React.SyntheticEvent, value?: unknown) => void;
    onMouseOver?: () => void;
};

class DropDownButton extends Component<
    DropDownButtonProps & ScrollAwayProps & ClickAwayProps & ThemeProps<DefaultTheme>,
    State
> {
    static defaultProps = defaultProps;

    state = {
        opened: false,
    };

    private button = createRef<HTMLButtonElement>();
    private menu: HTMLElement | null = null;
    private items: (HTMLElement | null)[] = [];

    private getFirstItem = () => this.items[0];
    private getLastItem = () => this.items[this.items.length - 1];

    private moveItemFocus = ({ indexDiff }: { indexDiff: number }) => {
        const activeIndex = this.items.indexOf(document.activeElement as HTMLElement);
        const nextIndex = mod(activeIndex + indexDiff, this.items.length);
        const nextItem = this.items[nextIndex];
        if (nextItem) {
            nextItem.focus();
        }
    };

    private open = ({ getFocusItem = this.getFirstItem } = {}) => {
        this.setState({ opened: true });

        window.setTimeout(() => {
            const focusAfterOpen = () => {
                if (this.menu) {
                    this.menu.removeEventListener('transitionend', focusAfterOpen);
                }

                window.setTimeout(() => {
                    const focusItem = getFocusItem();
                    if (focusItem) {
                        focusItem.focus();
                    }
                });
            };

            if (this.menu) {
                this.menu.addEventListener('transitionend', focusAfterOpen);
            }

            this.props.registerClickAway({ handler: () => this.close({ keepFocus: false }), roots: [() => this.menu] });
            this.props.registerScrollAway({ handler: this.close, roots: [() => this.menu] });
        });
    };

    private close = ({ keepFocus = true } = {}) => {
        this.setState({ opened: false });
        this.props.deregisterClickAway();
        this.props.deregisterScrollAway();

        if (keepFocus && this.button.current) {
            this.button.current.focus();
        }
    };

    private handleButtonClick = () => {
        if (!this.state.opened) {
            this.open();
        } else {
            this.close();
        }
    };

    private handleMenuButtonKeyDown = (event: React.KeyboardEvent) => {
        let handled = false;
        const key = event.key === ' ' ? KeyValue.Spacebar : event.key;
        switch (key) {
            case KeyValue.Enter:
            case KeyValue.Spacebar:
            case KeyValue.ArrowDown:
                if (!this.state.opened) {
                    this.open();
                    handled = true;
                }
                break;

            case KeyValue.ArrowUp:
                if (!this.state.opened) {
                    this.open({ getFocusItem: this.getLastItem });
                    handled = true;
                }
                break;

            default:
                break;
        }

        if (handled) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    private handleMenuKeyDown = (event: React.KeyboardEvent) => {
        let handled = false;
        const key = event.key === ' ' ? KeyValue.Spacebar : event.key;
        switch (key) {
            case KeyValue.ArrowDown:
                this.moveItemFocus({ indexDiff: 1 });
                handled = true;
                break;

            case KeyValue.ArrowUp:
                this.moveItemFocus({ indexDiff: -1 });
                handled = true;
                break;

            case KeyValue.Home:
            case KeyValue.PageUp: {
                const firstItem = this.getFirstItem();
                if (firstItem) {
                    firstItem.focus();
                    handled = true;
                }
                break;
            }

            case KeyValue.End:
            case KeyValue.PageDown: {
                const lastItem = this.getLastItem();
                if (lastItem) {
                    lastItem.focus();
                    handled = true;
                }
                break;
            }

            case KeyValue.Enter:
            case KeyValue.Spacebar:
                if (document.activeElement) {
                    (document.activeElement as HTMLElement).click();
                }
                handled = true;
                break;

            case KeyValue.Escape:
                this.close();
                handled = true;
                break;

            case KeyValue.Tab:
                this.close();
                // Event is not handled to focus next element in tab sequence
                break;

            default:
                break;
        }

        if (handled) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    handleMenuRef = (e: HTMLElement | null) => (this.menu = e);

    render() {
        const {
            children,
            disabled,
            label,
            size: customSize,
            theme,
            variant,
            wide,
            portalContainerId,
            ...other
        } = getProps(this.props);
        const { size } = getComponentProps(theme, 'Button', { size: customSize });

        let index = 0;
        const menuItems = Children.map(children, (c) => {
            if (!isValidElement<ChildProps>(c)) {
                return c;
            }

            const handleItemClick = (event: React.SyntheticEvent, value?: unknown) => {
                if (c.props.onClick) {
                    c.props.onClick(event, value);
                }
                this.close();
            };

            const elementIndex = index++;
            const handleItemRef = (element: HTMLElement) => {
                this.items[elementIndex] = element;
            };
            const handleMouseOver = () => {
                const item = this.items[elementIndex];
                if (item) {
                    item.focus();
                }
            };

            return cloneElement(c, {
                onClick: handleItemClick,
                onMouseOver: handleMouseOver,
                innerRef: handleItemRef,
                tabIndex: -1,
                role: 'menuitem',
            });
        });

        return (
            <>
                <Button
                    aria-haspopup="true"
                    aria-expanded={this.state.opened}
                    disabled={disabled}
                    onClick={this.handleButtonClick}
                    size={size}
                    variant={variant}
                    wide={wide}
                    onKeyDown={this.handleMenuButtonKeyDown}
                    ref={this.button}
                    {...other}
                >
                    <ContentElement>
                        <div>{label}</div>
                        <ExpandIcon disabled={disabled} expanded={this.state.opened} size={size} />
                    </ContentElement>
                </Button>
                <Menu
                    portalContainerId={portalContainerId}
                    anchorElement={this.button.current}
                    sizeToAnchor={wide}
                    opened={this.state.opened}
                    onKeyDown={this.handleMenuKeyDown}
                    role="menu"
                    overlayMinWidth={160}
                    ref={this.handleMenuRef}
                >
                    {menuItems}
                </Menu>
            </>
        );
    }
}

export default withTheme(withScrollAway(withClickAway(DropDownButton)));
